import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Sparkles, MailOpen, X } from "lucide-react";
import { api } from "@/lib/api";

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ counts: { total: 0, pending_applications: 0, open_tickets: 0 },
                                     recent_applications: [], recent_tickets: [] });
  const pollRef = useRef(null);
  const nav = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/admin/notifications");
      setData(data);
    } catch (err) { console.warn("notifications poll error:", err); }
  };

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 30000);
    return () => clearInterval(pollRef.current);
  }, []);

  useEffect(() => {
    const close = (e) => {
      if (!e.target.closest?.("[data-notif-root]")) setOpen(false);
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  const total = data.counts.total || 0;

  const goTo = (anchor) => {
    setOpen(false);
    nav("/counselor");
    setTimeout(() => {
      document.getElementById(anchor)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 250);
  };

  return (
    <div className="relative" data-notif-root>
      <button data-testid="notif-bell" onClick={() => { setOpen(o => !o); load(); }}
        aria-label="Notifications"
        className="relative rounded-full w-10 h-10 flex items-center justify-center btn-outline"
        style={{padding:0}}>
        <Bell className="w-4 h-4"/>
        {total > 0 && (
          <span data-testid="notif-badge"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-[0.65rem] font-bold flex items-center justify-center"
            style={{background:"var(--accent)", color:"#fff"}}>
            {total > 9 ? "9+" : total}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notif-panel"
          className="absolute right-0 mt-2 w-80 rounded-2xl shadow-2xl z-50 overflow-hidden"
          style={{background:"#fff", border:"1px solid var(--border-soft)"}}>
          <div className="flex items-center justify-between px-4 py-3 border-b" style={{borderColor:"var(--border-soft)"}}>
            <div className="font-serif-display text-lg">Notifications</div>
            <button onClick={() => setOpen(false)} className="text-[color:var(--text-secondary)]">
              <X className="w-4 h-4"/>
            </button>
          </div>

          {total === 0 ? (
            <div className="p-6 text-sm text-center" style={{color:"var(--text-secondary)"}}>
              You're all caught up. ✨
            </div>
          ) : (
            <div className="max-h-96 overflow-y-auto">
              {data.counts.pending_applications > 0 && (
                <div>
                  <button onClick={() => goTo("apps-section")}
                    data-testid="notif-apps-header"
                    className="w-full px-4 py-2 flex items-center justify-between text-left"
                    style={{background:"rgba(91,117,83,0.06)"}}>
                    <span className="text-xs uppercase tracking-widest inline-flex items-center gap-2" style={{color:"var(--brand)"}}>
                      <Sparkles className="w-3.5 h-3.5"/> Applications
                    </span>
                    <span className="chip text-[0.6rem]">{data.counts.pending_applications}</span>
                  </button>
                  {data.recent_applications.map(a => (
                    <button key={a.id} data-testid={`notif-app-${a.id}`}
                      onClick={() => goTo("apps-section")}
                      className="w-full text-left px-4 py-3 border-b hover:bg-[color:var(--bg-alt)]/40 transition"
                      style={{borderColor:"var(--border-soft)"}}>
                      <div className="text-sm font-medium truncate">{a.name}</div>
                      <div className="text-xs mt-0.5" style={{color:"var(--text-secondary)"}}>{a.specialty}</div>
                      <div className="text-xs mt-0.5" style={{color:"var(--brand)"}}>Review →</div>
                    </button>
                  ))}
                </div>
              )}
              {data.counts.open_tickets > 0 && (
                <div>
                  <button onClick={() => goTo("tickets-section")}
                    data-testid="notif-tickets-header"
                    className="w-full px-4 py-2 flex items-center justify-between text-left"
                    style={{background:"rgba(230,178,58,0.08)"}}>
                    <span className="text-xs uppercase tracking-widest inline-flex items-center gap-2" style={{color:"var(--accent)"}}>
                      <MailOpen className="w-3.5 h-3.5"/> Mooly escalations
                    </span>
                    <span className="chip text-[0.6rem]" style={{background:"rgba(230,178,58,0.15)", color:"var(--accent)"}}>{data.counts.open_tickets}</span>
                  </button>
                  {data.recent_tickets.map(t => (
                    <button key={t.id} data-testid={`notif-ticket-${t.id}`}
                      onClick={() => goTo("tickets-section")}
                      className="w-full text-left px-4 py-3 border-b hover:bg-[color:var(--bg-alt)]/40 transition"
                      style={{borderColor:"var(--border-soft)"}}>
                      <div className="text-sm font-medium truncate">{t.subject}</div>
                      <div className="text-xs mt-0.5" style={{color:"var(--text-secondary)"}}>from {t.visitor_name || "anonymous"}</div>
                      <div className="text-xs mt-0.5" style={{color:"var(--accent)"}}>Open →</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
